import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus, Shield, UserMinus, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config';

const CollaborationModal = ({ taskId, taskName, onClose, requesterEmail }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCollaborators();
    }, [taskId]);

    const fetchCollaborators = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/collaborators`);
            const data = await res.json();
            if (data.collaborators) setCollaborators(data.collaborators);
        } catch (err) {
            console.error("Failed to fetch collaborators:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (val) => {
        setSearchQuery(val);
        if (val.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/faculty-search?query=${val}`);
            const data = await res.json();
            if (data.faculty) setSearchResults(data.faculty);
        } catch (err) {
            console.error("Search failed:", err);
        } finally {
            setSearchLoading(false);
        }
    };

    const addCollaborator = async (faculty) => {
        setError(null);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/collaborators`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: faculty.email, requester_email: requesterEmail })
            });
            const data = await res.json();
            if (data.success) {
                setSearchResults([]);
                setSearchQuery('');
                fetchCollaborators();
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError("Failed to add collaborator");
        }
    };

    const removeCollaborator = async (email) => {
        if (!window.confirm("Are you sure you want to revert access for this faculty?")) return;
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/collaborators/${email}?requester_email=${requesterEmail}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                fetchCollaborators();
            }
        } catch (err) {
            console.error("Failed to remove collaborator:", err);
        }
    };

    return (
        <div className="modal-overlay">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card"
                style={{ width: '90%', maxWidth: '500px', padding: '2rem', position: 'relative' }}
            >
                <button onClick={onClose} className="close-btn" style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                    <X size={24} />
                </button>

                <h2 className="brand-title" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Manage Access</h2>
                <p style={{ opacity: 0.7, marginBottom: '1.5rem' }}>Room: {taskName}</p>

                <div className="search-box-modern" style={{ marginBottom: '1.5rem' }}>
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Search faculty by name or Emp ID..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="error-badge" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle size={16} /> {error}
                    </div>
                )}

                <AnimatePresence>
                    {searchResults.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="glass-card"
                            style={{ position: 'absolute', width: 'calc(100% - 4rem)', zIndex: 10, padding: '0.5rem', marginTop: '-1rem' }}
                        >
                            {searchResults.map(f => (
                                <button
                                    key={f.email}
                                    className="search-result-row"
                                    style={{ width: '100%', justifyContent: 'space-between', padding: '0.75rem' }}
                                    onClick={() => addCollaborator(f)}
                                >
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontWeight: '600' }}>{f.name}</div>
                                        <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>{f.emp_id} • {f.email}</div>
                                    </div>
                                    <UserPlus size={18} />
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="collaborators-list">
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Shield size={18} /> Faculty Coordinators
                    </h3>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '1rem' }}>Loading collaborators...</div>
                    ) : collaborators.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.5 }}>No collaborators yet</div>
                    ) : (
                        collaborators.map(c => (
                            <div key={c.email} className="search-result-row" style={{ width: '100%', justifyContent: 'space-between', padding: '0.75rem', cursor: 'default' }}>
                                <div style={{ textAlign: 'left' }}>
                                    <div style={{ fontWeight: '600' }}>{c.name}</div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>{c.emp_id} • {c.email}</div>
                                </div>
                                <button
                                    className="icon-btn-danger"
                                    style={{ color: 'var(--danger-color)', opacity: 0.8 }}
                                    onClick={() => removeCollaborator(c.email)}
                                    title="Revert Access"
                                >
                                    <UserMinus size={18} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default CollaborationModal;
